import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PageShell } from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import {
  Backdrop,
  Hero,
  HomeContentSections,
  usePublicHomeData,
} from '@/components/home';
import { useMe } from '@/features/auth/api';
import { ApiRequestError } from '@/lib/api';

/** Public home. The page is readable to everyone; every module shown here
 *  is backed by the same visibility-filtered APIs used by the feature
 *  pages, so guests only see public/readable rows and signed-in viewers
 *  naturally get their broader readable slice. */
function HomeRoute(): ReactNode {
  const me = useMe();
  const navigate = useNavigate();
  const home = usePublicHomeData();

  const isAuthenticated =
    !(me.error instanceof ApiRequestError && me.error.status === 401) &&
    me.data != null;

  return (
    <div className="home-page">
      <Backdrop />

      <PageShell className="home-shell">
        <Hero
          featuredArticle={home.featuredArticle}
          stats={[
            { id: 'articles', value: home.totals.articles },
            { id: 'groups', value: home.totals.groups },
            { id: 'events', value: home.totals.events },
          ]}
          isAuthenticated={isAuthenticated}
          onSignIn={() => {
            void navigate({ to: '/login', search: { next: '/' } });
          }}
          onCreate={() => {
            void navigate({ to: '/register' });
          }}
          onDashboard={() => {
            void navigate({ to: '/dashboard' });
          }}
        />
        <HomeContentSections
          articles={home.latestArticles}
          groups={home.publicGroups}
          events={home.upcomingEvents}
          categories={home.categoryList}
          tags={home.tagList}
          isLoading={home.isLoading}
          isAuthenticated={isAuthenticated}
          onRequireLogin={(next) => {
            void navigate({ to: '/login', search: { next } });
          }}
        />
      </PageShell>
    </div>
  );
}

export const Route = createFileRoute('/')({
  component: HomeRoute,
});
